import { EndpointDialog } from "@/components/models/EndpointDialog";
import { ModelJobs, SaveBar, type PickerOpen } from "@/components/models/ModelJobs";
import { ModelPicker, type PickerAddOwn, type PickerModel, type PickerSpecial } from "@/components/models/ModelParts";
import { firstModelFor, pickerModelsFor } from "@/components/models/picker-models";
import { endpointKind } from "@/components/models/vendor-logos";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import {
  api,
  ApiError,
  type BooModelCatalog,
  type BooModels,
  type EffectiveModelRow,
  type PurposeDefView,
} from "@/lib/api";
import { describeModelError } from "@/lib/model-errors";
import {
  acceptsCustom,
  ASK_ADMIN,
  BUILT_IN_ONLY,
  isChatClass,
  jobLabel,
  joinNames,
  MODEL_OFF,
  modelName,
  OFF_CONSEQUENCE,
  sourceLabel,
} from "@/lib/models";
import { Notice } from "@/pages/container-tabs/connections/ConnectionParts";
import { ManageModelsDialog } from "@/pages/org-models/ManageModels";
import { useInvalidateModels } from "@/pages/org-models/use-org-models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { LegacyEndpoints } from "./LegacyEndpoints";

// A Boo's Models tab: the Boo's own picks over the org default for each job,
// from the models the organization has on (built-in ones and its own). Switching applies on the Boo's next
// turn, without a restart or a rebuild. Org owners and admins also reach
// Manage models from here.

const ORG = "";

export function ModelsTab() {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto p-6">
      <ModelsTabBody />
    </div>
  );
}

function ModelsTabBody() {
  const { containerId } = useParams({ strict: false }) as { containerId: string };
  const query = useQuery({
    queryKey: ["boo-models", containerId],
    queryFn: () => api.models.boo(containerId),
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 2,
  });
  const catalogQuery = useQuery({
    queryKey: ["boo-model-catalog", containerId],
    queryFn: () => api.models.booCatalog(containerId),
    enabled: !!query.data?.brain,
    staleTime: 5 * 60 * 1000,
  });

  if (query.isLoading) return <LoadingIndicator />;
  if (query.isError || !query.data) return <ErrorComponent error={query.error ?? "Failed to load models"} />;
  const data = query.data;

  if (!data.brain || !data.purposeDefs || !data.effective) {
    return (
      <div className="max-w-3xl">
        <Notice tone="warning" action={<Button size="sm" variant="outline" onClick={() => query.refetch()}>Try again</Button>}>
          <p className="font-medium">
            {data.brainError === "brain_not_ready" ? "This Boo isn't running." : "Can't reach this Boo right now."}
          </p>
          <p className="text-muted-foreground">
            {data.brainError === "brain_not_ready"
              ? "Start it to see and change its models."
              : "Try again in a few seconds."}
          </p>
        </Notice>
      </div>
    );
  }
  if (catalogQuery.isLoading) return <LoadingIndicator />;
  if (catalogQuery.isError) {
    return (
      <div className="max-w-3xl">
        <Notice tone="warning" action={<Button size="sm" variant="outline" onClick={() => catalogQuery.refetch()}>Try again</Button>}>
          <p className="font-medium">The model list didn&apos;t load.</p>
          <p className="text-muted-foreground">This Boo couldn&apos;t fetch it just now. Try again in a few seconds.</p>
        </Notice>
      </div>
    );
  }
  return (
    <ModelsEditor
      containerId={containerId}
      data={data as BooData}
      catalog={catalogQuery.data ?? null}
      onChanged={() => void query.refetch()}
    />
  );
}

const PROBLEM_COPY: Record<string, (source: string) => string> = {
  vault_ref_unresolved: (source) => `Won't run: the access key for ${source} needs to be entered again.`,
  org_key_unavailable: (source) => `Won't run: the access key for ${source} needs to be entered again.`,
  gateway_unconfigured: () => "Won't run: built-in models aren't set up on this Boo.",
};

const SKIPPED_COPY: Record<NonNullable<EffectiveModelRow["skipped"]>["reason"], string> = {
  model_disabled: "is turned off for your organization",
  model_removed: "was removed from your own models",
  key_unavailable: "can't run",
};

// Where the running model comes from, in the Boo's own words (the effective
// row's source), never inferred from the pick.
const SOURCE_COPY: Record<string, string> = {
  configured: "chosen for this Boo",
  "org-default": "organization default",
  "chat-fallback": "same as Conversations",
  default: "built-in default",
  "thread-flag": "set for one conversation",
};

// A line under a job's dropdown, only when what runs isn't what the dropdown
// says: a problem, a skipped pick, or an older Boo still running something.
// It names the model that actually runs and where it comes from.
function StatusLine({
  row,
  expected,
  nameOf,
  sourceOf,
  stillOff,
  waitingForUpdate,
  sourceOfSlug,
  onEnterKey,
}: {
  row: EffectiveModelRow | undefined;
  // What the dropdown's choice runs and where from, as far as the page can tell.
  expected: { model: string | null; source: string };
  nameOf: (slug: string) => string;
  sourceOf: (provider: string | null) => string;
  // The saved pick is turned off but this Boo hasn't updated yet.
  stillOff: string | null;
  // The choice is one of your own models, which this Boo can't run yet.
  waitingForUpdate?: boolean;
  // The display name of the service a model of your own comes from.
  sourceOfSlug: (slug: string) => string;
  // Org admins: open Manage models on the service whose key is missing.
  onEnterKey?: (slug: string) => void;
}) {
  if (!row) return null;
  const problem = typeof row.problem === "string" ? row.problem : row.problem?.code;
  if (problem) {
    return (
      <p className="text-xs font-medium text-destructive">
        {PROBLEM_COPY[problem]?.(sourceOf(row.provider)) ?? "This job can't run right now."}
      </p>
    );
  }
  if (stillOff) {
    return (
      <p className="text-xs text-amber-700 dark:text-amber-400">
        Still running {nameOf(stillOff)} until this Boo updates.
      </p>
    );
  }
  const running = row.model ? (
    <>
      <span className="font-medium">{nameOf(row.model)}</span>
      {SOURCE_COPY[row.source] && ` (${SOURCE_COPY[row.source]})`}
    </>
  ) : (
    <span className="font-medium">nothing</span>
  );
  if (row.skipped?.reason === "key_unavailable") {
    const skipped = row.skipped.model;
    return (
      <p className="text-xs text-amber-700 dark:text-amber-400">
        {nameOf(skipped)} can&apos;t run: the access key for {sourceOfSlug(skipped)} needs to be entered again.
        {onEnterKey && (
          <>
            {" "}
            <button
              type="button"
              className="font-medium text-foreground underline-offset-2 hover:underline"
              onClick={() => onEnterKey(skipped)}
            >
              Enter it in Manage models
            </button>
          </>
        )}
      </p>
    );
  }
  if (row.skipped) {
    return (
      <p className="text-xs text-amber-700 dark:text-amber-400">
        {nameOf(row.skipped.model)} {SKIPPED_COPY[row.skipped.reason] ?? "can't run"}, so Boo uses {running} instead.
      </p>
    );
  }
  if ((row.model ?? null) === expected.model && (row.source === expected.source || !row.model)) return null;
  if (waitingForUpdate && row.model) {
    return <p className="text-xs text-amber-700 dark:text-amber-400">Uses {running} until this Boo updates.</p>;
  }
  if (!row.model) return <p className="text-xs text-muted-foreground">Running now: Off</p>;
  return <p className="text-xs text-muted-foreground">Running now: {running}</p>;
}

type BooData = BooModels & { purposeDefs: PurposeDefView[]; effective: NonNullable<BooModels["effective"]> };

function ModelsEditor({
  containerId,
  data,
  catalog,
  onChanged,
}: {
  containerId: string;
  data: BooData;
  catalog: BooModelCatalog | null;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateModels();
  const navigate = useNavigate();
  const { orgId } = useParams({ strict: false }) as { orgId?: string };
  const canConfigure = data._permissions.canConfigure;
  const canManageOrgModels = data._permissions.canManageOrgModels;
  const [draft, setDraft] = useState<Record<string, string>>(data.selection);
  // When the Boo's picks change underneath (our save landing, a chat-side pick,
  // another admin), follow them unless the user has unsaved edits.
  const [base, setBase] = useState(data.selection);
  if (JSON.stringify(sortObj(base)) !== JSON.stringify(sortObj(data.selection))) {
    // Carry over only what the user changed; everything else takes the new
    // server value, so saving can't revert someone else's change.
    const next = { ...data.selection };
    for (const key of new Set([...Object.keys(draft), ...Object.keys(base)])) {
      if ((draft[key] || "") === (base[key] || "")) continue;
      if (draft[key]) next[key] = draft[key];
      else delete next[key];
    }
    setDraft(next);
    setBase(data.selection);
  }
  const [managing, setManaging] = useState(false);
  // The service Manage models opens on, to enter its key again.
  const [editEndpoint, setEditEndpoint] = useState<string | null>(null);
  const [openJob, setOpenJob] = useState<string | null>(null);
  // The job whose dropdown opened "Add your own model".
  const [addingFor, setAddingFor] = useState<PurposeDefView | null>(null);
  const dirty = JSON.stringify(sortObj(draft)) !== JSON.stringify(sortObj(data.selection));
  const supportsOrgEndpoints = !!data.brain?.supportsOrgEndpoints;
  const enforcesDisabled = !!data.brain?.enforcesDisabledModels;
  const legacy = data.providers;
  const offSet = new Set(data.disabledModels);

  const sources = [...legacy.map((p) => ({ name: p.name, baseUrl: p.baseUrl, label: null })), ...data.orgEndpoints];
  const sourceOfSlug = (slug: string | null | undefined) => sources.find((p) => !!slug && slug.startsWith(`${p.name}/`));
  const isCustom = (slug: string | null | undefined) => !!sourceOfSlug(slug);
  const nameOf = (slug: string) => modelName(slug, { custom: isCustom(slug) });
  const sourceOf = (provider: string | null) => {
    const src = sources.find((p) => p.name === provider);
    return src ? sourceLabel(src) : "your own models";
  };

  // Only what the user changed: every other entry in the Boo's file (picks
  // made in chat included) is left alone. null = no pick, follow the default.
  const edits = (): Record<string, string | null> => {
    const out: Record<string, string | null> = {};
    for (const key of new Set([...Object.keys(draft), ...Object.keys(data.selection)])) {
      if ((draft[key] || "") !== (data.selection[key] || "")) out[key] = draft[key] || null;
    }
    return out;
  };

  const save = useMutation({
    mutationFn: () => api.models.setBoo(containerId, edits()),
    onSuccess: (res) => {
      toast.success(res.changed ? "Saved. Boo uses the new models from its next reply." : "Already running these models.");
      onChanged();
    },
    onError: (err) => toast.error(describeModelError(err, { defs: data.purposeDefs, isCustom })),
  });

  const effectiveOf = (purpose: string) => data.effective.effective.find((e) => e.purpose === purpose);

  const modelsFor = (def: PurposeDefView): PickerModel[] => {
    const allow = acceptsCustom(def);
    // This Boo's own services from before your organization's: still usable.
    const own: PickerModel[] = legacy.flatMap((p) =>
      def.role !== "chat"
        ? []
        : p.models
            .filter((id) => allow || draft[def.id] === `${p.name}/${id}`)
            .map((id) => ({
              name: `${p.name}/${id}`,
              endpoint: p.name,
              endpointLabel: `${sourceLabel(p)} (only on this Boo)`,
              endpointKind: endpointKind(p.baseUrl, p.name),
              disabled: !allow,
              note: !allow ? "Built-in models only" : p.keyNeeded ? "Access key needed" : undefined,
            })),
    );
    const org = pickerModelsFor({
      purpose: def,
      rows: (catalog?.models ?? []).filter((r) => !(r.endpoint && legacy.some((p) => p.name === r.endpoint))),
      endpoints: data.orgEndpoints,
      current: draft[def.id],
      customBlockedNote: supportsOrgEndpoints ? undefined : "Available after this Boo updates",
    });
    return [...own, ...org];
  };

  const addOwnFor = (def: PurposeDefView): PickerAddOwn | undefined => {
    if (!canConfigure) return undefined;
    if (!acceptsCustom(def)) return { hint: BUILT_IN_ONLY };
    return canManageOrgModels ? { onSelect: () => setAddingFor(def) } : { hint: ASK_ADMIN };
  };

  // "No pick" for this job: the org default when the Boo fetches one,
  // otherwise what the Boo does without a setting.
  const orgApplies = !!data.brain?.orgDefaultsApply;
  const builtInOf = (def: PurposeDefView): string | null => {
    const row = effectiveOf(def.id);
    if (!data.selection[def.id] && row?.source === "default") return row.model;
    return def.defaultModel;
  };
  const specialsFor = (def: PurposeDefView): PickerSpecial[] => {
    const orgDefault = orgApplies ? data.orgDefaults[def.id] : undefined;
    const off: PickerSpecial[] =
      isChatClass(def) ? [] : [{ value: MODEL_OFF, label: "Off", detail: OFF_CONSEQUENCE[def.id] ?? "Boo won't do this job" }];
    if (orgDefault) return [{ value: ORG, label: "Organization default", detail: nameOf(orgDefault) }, ...off];
    if (isChatClass(def) && def.id !== "chat") return [{ value: ORG, label: "Same as Conversations" }];
    // Older Boos must keep an explicit conversation model.
    if (def.id === "chat" && !orgApplies) return [];
    const model = builtInOf(def);
    const turnedOff = !!model && offSet.has(model);
    return [
      {
        value: ORG,
        label: "Built-in default",
        detail: model ? `${modelName(model)}${turnedOff ? " (turned off)" : ""}` : undefined,
        disabled: turnedOff,
      },
      ...off,
    ];
  };

  // What the saved choice for a job should run, to tell when the Boo runs
  // something else.
  const expectedOf = (def: PurposeDefView): { model: string | null; source: string } => {
    const pick = data.selection[def.id];
    if (pick === MODEL_OFF) return { model: null, source: "unset" };
    if (pick) return { model: pick, source: "configured" };
    if (orgApplies && data.orgDefaults[def.id]) return { model: data.orgDefaults[def.id], source: "org-default" };
    if (isChatClass(def) && def.id !== "chat") return { model: effectiveOf("chat")?.model ?? null, source: "chat-fallback" };
    return { model: builtInOf(def), source: "default" };
  };

  const chatModel = effectiveOf("chat")?.model;
  const keyNeeded = [...legacy, ...data.orgEndpoints].filter((p) => p.keyNeeded);
  // Picks the org turned off that this Boo still runs until it updates.
  const stillRunningOff = enforcesDisabled
    ? []
    : [...new Set(Object.values(data.selection).filter((slug) => offSet.has(slug)))];
  const needsUpdate = (!supportsOrgEndpoints && data.orgEndpoints.length > 0) || stillRunningOff.length > 0;

  const picker = (def: PurposeDefView, big: boolean, open: PickerOpen) => (
    <ModelPicker
      ariaLabel={`Model for ${jobLabel(def)}`}
      value={draft[def.id] ?? ORG}
      onChange={(v) => setDraft((d) => withPick(d, def.id, v))}
      models={modelsFor(def)}
      specials={specialsFor(def)}
      disabled={!canConfigure}
      addOwn={addOwnFor(def)}
      className={big ? "h-11" : undefined}
      open={open.open}
      onOpenChange={open.onOpenChange}
    />
  );

  const status = (def: PurposeDefView) => {
    const pick = data.selection[def.id];
    return (
      <StatusLine
        row={effectiveOf(def.id)}
        expected={expectedOf(def)}
        nameOf={nameOf}
        sourceOf={sourceOf}
        sourceOfSlug={(slug) => {
          const src = sourceOfSlug(slug);
          return src ? sourceLabel(src) : "your own models";
        }}
        onEnterKey={
          canManageOrgModels
            ? (slug) => {
                setEditEndpoint(data.orgEndpoints.find((e) => slug.startsWith(`${e.name}/`))?.name ?? null);
                setManaging(true);
              }
            : undefined
        }
        stillOff={!enforcesDisabled && pick && offSet.has(pick) && effectiveOf(def.id)?.model === pick ? pick : null}
        waitingForUpdate={!supportsOrgEndpoints && data.orgEndpoints.some((e) => expectedOf(def).model?.startsWith(`${e.name}/`))}
      />
    );
  };

  const updateEffects = [
    !supportsOrgEndpoints && data.orgEndpoints.length > 0 && "can't use your organization's own models",
    stillRunningOff.length > 0 && `still runs ${joinNames(stillRunningOff.map(nameOf))}, which ${stillRunningOff.length === 1 ? "is" : "are"} turned off`,
  ].filter(Boolean) as string[];

  const notices = (
    <>
      {needsUpdate && (
        <Notice tone="info">
          <p className="font-medium">Waiting for an update.</p>
          <p className="text-muted-foreground">
            This Boo updates automatically, usually within a few days. Until then it {joinNames(updateEffects)}.
          </p>
        </Notice>
      )}
      {keyNeeded.map((p) => (
        <Notice key={p.name} tone="warning">
          <p className="font-medium">The access key for {sourceLabel(p)} needs to be entered again.</p>
          <p className="text-muted-foreground">
            Until then, jobs that use it won&apos;t run.
            {data.orgEndpoints.some((e) => e.name === p.name) &&
              (canManageOrgModels ? " Edit it in Manage models to enter it." : " Ask an organization admin to enter it.")}
          </p>
        </Notice>
      ))}
      {!orgApplies && Object.keys(data.orgDefaults).length > 0 && (
        <Notice tone="info">
          <p className="font-medium">Organization defaults reach this Boo with its next update.</p>
          <p className="text-muted-foreground">Until then it runs the models set here.</p>
        </Notice>
      )}
      {data.brain?.fileState === "invalid" && (
        <Notice tone="warning">
          <p className="font-medium">This Boo&apos;s model settings couldn&apos;t be read, so it runs on built-in defaults.</p>
          <p className="text-muted-foreground">Saving here writes fresh settings.</p>
        </Notice>
      )}
    </>
  );

  return (
    <>
      <ModelJobs
        intro="Choose the model this Boo uses for each job, from the models your organization has on, or follow the organization default. Changes apply from Boo's next reply."
        managedBy={canConfigure ? null : "Managed by this Boo's admins"}
        onManage={canManageOrgModels ? () => setManaging(true) : undefined}
        notices={notices}
        jobs={data.purposeDefs}
        picker={picker}
        status={status}
        openJob={openJob}
        onOpenJobChange={setOpenJob}
        shownByDefault={(def) => {
          const model = effectiveOf(def.id)?.model;
          return !!draft[def.id] || (orgApplies && !!data.orgDefaults[def.id]) || (!!model && model !== chatModel);
        }}
      >
        {legacy.length > 0 && (
          <LegacyEndpoints
            containerId={containerId}
            providers={legacy}
            canConfigure={canConfigure}
            canManageOrgModels={canManageOrgModels}
            supportsOrgEndpoints={supportsOrgEndpoints}
            orgEndpointNames={data.orgEndpoints.map((e) => e.name)}
            reservedNames={catalog?.reservedProviderNames ?? ["gateway"]}
            purposeDefs={data.purposeDefs}
          />
        )}
        {canConfigure && dirty && (
          <SaveBar
            detail="Applies from Boo's next reply."
            saving={save.isPending}
            onDiscard={() => setDraft(data.selection)}
            onSave={() => save.mutate()}
          />
        )}
      </ModelJobs>

      {canManageOrgModels && (
        <ManageModelsDialog
          open={managing}
          onOpenChange={(o) => {
            setManaging(o);
            if (!o) setEditEndpoint(null);
          }}
          editEndpoint={editEndpoint}
          onChooseFor={(job) => {
            // A default is the organization's: change it on its Models page.
            setManaging(false);
            void navigate({ href: `/orgs/${orgId ?? ""}/models?job=${encodeURIComponent(job)}` });
          }}
        />
      )}

      {canManageOrgModels && (
        <EndpointDialog
          open={addingFor !== null}
          onOpenChange={(o) => !o && setAddingFor(null)}
          initialRole={addingFor?.role}
          takenNames={data.orgEndpoints.map((e) => e.name)}
          reservedNames={catalog?.reservedProviderNames ?? ["gateway"]}
          purposeDefs={data.purposeDefs}
          onSaved={async (endpoint) => {
            const purpose = addingFor;
            setAddingFor(null);
            // Wait for the new models to be listed, then pick one where the
            // flow started.
            await Promise.all([
              queryClient.refetchQueries({ queryKey: ["boo-model-catalog", containerId] }),
              queryClient.refetchQueries({ queryKey: ["boo-models", containerId] }),
            ]);
            void invalidate();
            const slug = purpose && firstModelFor(endpoint, purpose);
            if (!purpose || !slug) return;
            if (!supportsOrgEndpoints) {
              toast.info("Saved for your organization. This Boo can use it once it updates.");
              return;
            }
            setDraft((d) => withPick(d, purpose.id, slug));
          }}
        />
      )}
    </>
  );
}

function withPick(d: Record<string, string>, purpose: string, value: string): Record<string, string> {
  const next = { ...d };
  if (value) next[purpose] = value;
  else delete next[purpose];
  return next;
}

function sortObj(o: Record<string, string>): Array<[string, string]> {
  return Object.entries(o).filter(([, v]) => v).sort(([a], [b]) => a.localeCompare(b));
}
